import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function Performance() {
  const [metrics] = useState({
    nca: { current: 250, target: 300, trend: 'up' },
    ltv: { current: 450, target: 500, trend: 'up' },
    amer: { current: 3.2, target: 4.0, trend: 'up' },
    margin: { current: 42, target: 45, trend: 'down' }
  });

  const [performanceData] = useState([
    { date: '2024-01', nca: 200, ltv: 400, amer: 2.8, margin: 40 },
    { date: '2024-02', nca: 220, ltv: 420, amer: 2.9, margin: 41 },
    { date: '2024-03', nca: 250, ltv: 450, amer: 3.2, margin: 42 }
  ]);

  const [alerts] = useState([
    {
      type: 'warning',
      message: 'Customer acquisition cost increased by 15% this week',
      action: 'Review'
    },
    {
      type: 'success',
      message: 'Email campaign performance exceeded target by 25%',
      action: 'View Details'
    },
    {
      type: 'warning',
      message: 'Profit margin below target in Category A',
      action: 'Analyze'
    }
  ]);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Performance Tracking</h1>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {Object.entries(metrics).map(([key, data]) => (
            <div key={key} className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="text-sm font-medium text-gray-500 uppercase">{key.toUpperCase()}</h3>
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <p className="text-2xl font-semibold text-gray-900">{data.current}</p>
                  <p className="text-sm text-gray-500">Target: {data.target}</p>
                </div>
                <span className={data.trend === 'up' ? 'text-green-500' : 'text-red-500'}>
                  {data.trend === 'up' ? '↑' : '↓'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Performance Chart */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Trends</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="nca" stroke="#4F46E5" name="NCA" />
                <Line type="monotone" dataKey="ltv" stroke="#9333EA" name="LTV" />
                <Line type="monotone" dataKey="amer" stroke="#16A34A" name="AMER" />
                <Line type="monotone" dataKey="margin" stroke="#DC2626" name="Margin" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Performance Alerts</h2>
          <div className="space-y-4">
            {alerts.map((alert, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg ${
                  alert.type === 'warning' ? 'bg-yellow-50' : 'bg-green-50'
                }`}
              >
                <div className="flex items-center">
                  <span
                    className={`mr-3 ${
                      alert.type === 'warning' ? 'text-yellow-400' : 'text-green-400'
                    }`}
                  >
                    {alert.type === 'warning' ? '⚠️' : '✓'}
                  </span>
                  <span className="text-sm text-gray-700">{alert.message}</span>
                </div>
                <button
                  className={`text-sm font-medium ${
                    alert.type === 'warning'
                      ? 'text-yellow-700 hover:text-yellow-800'
                      : 'text-green-700 hover:text-green-800'
                  }`}
                >
                  {alert.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Performance;